import { BaseResponseDto } from '@/models/dtos/baseReponseDto'
import { ProjectDto } from '@/models/dtos/projectDto'
import { Endpoints } from './_endpoints'
import axios from 'axios'
import { SimpleProjectDto } from '@/models/dtos/simpleProjectDto'
import { ProjectAttachmentDto } from '@/models/dtos/projectAttachmentDto'
import { ProjectStatisticsDto } from '@/models/dtos/projectStatisticsDto'

export abstract class ProjectService {
  static async getProjects(): Promise<SimpleProjectDto[]> {
    const result = await axios
      .get<BaseResponseDto<SimpleProjectDto[]>>(Endpoints.project.getProjects)

    if (!result.data.dto) return []

    return result.data.dto
  }

  static async getIncativeProjects(): Promise<SimpleProjectDto[]> {
    const result = await axios
      .get<BaseResponseDto<SimpleProjectDto[]>>(Endpoints.project.getInactiveProjects)

    if (!result.data.dto) return []

    return result.data.dto
  }

  static async getProject(projectId: string): Promise<ProjectDto> {
    const result = await axios
      .get<BaseResponseDto<ProjectDto>>(Endpoints.project.getProject, {
        params: {
          projectId: projectId
        }
      })

    if (!result.data.dto) throw new Error('Cannot get data at projectService.getProject')

    return result.data.dto
  }

  static async getProjectStats(projectId: string): Promise<ProjectStatisticsDto> {
    const result = await axios
      .get<BaseResponseDto<ProjectStatisticsDto>>(Endpoints.project.getProjectStats,
        {
          params: {
            id: projectId
          }
        })

    if (!result.data.dto) throw new Error('Cannot get data')

    return result.data.dto
  }

  static async saveProject(project: ProjectDto): Promise<string> {
    const result = await axios
      .post<BaseResponseDto<string>>(Endpoints.project.saveProject, project)

    return result.data.dto!
  }

  static async editProject(project: ProjectDto): Promise<void> {
    await axios
      .put<BaseResponseDto<any>>(Endpoints.project.editProject, project)
  }

  static async removeProject(projectId: string): Promise<void> {
    await axios.delete(Endpoints.project.removeProject, {
      params: {
        id: projectId
      }
    })
  }

  // Attachments

  static async uploadProjectFile(files: FormData, projectId: string): Promise<any> {
    const result = await axios
      .post<BaseResponseDto<any>>(Endpoints.project.uploadFiles, files, {
        params: {
          projectId: projectId
        },
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

    return result?.data?.dto!
  }

  static async getProjectAttachments(projectId: string): Promise<ProjectAttachmentDto[]> {
    const result = await axios
      .get<BaseResponseDto<ProjectAttachmentDto[]>>(Endpoints.project.getProjectAttachments, {
        params: {
          projectId: projectId
        }
      })

    if (!result.data.dto) throw new Error('Cannot get data at projectService.getProjectAttachments')

    return result.data.dto
  }

  static async removeAttachment(attachmentId: string): Promise<void> {
    await axios
      .delete<BaseResponseDto<void>>(Endpoints.project.removeAttachment, {
        params: {
          attachmentId: attachmentId
        }
      })
  }

  static async donwloadAttachment(attachmentId: string): Promise<void> {
    const file = await axios
      .get(Endpoints.project.downloadAttachment, {
        params: {
          attachmentId: attachmentId
        },
        responseType: 'blob'
      })

    // Get server filename
    let filename = ''
    const disposition = file.request.getResponseHeader('Content-Disposition')

    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const matches = filenameRegex.exec(disposition)
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '')
      }
    }

    // Download file
    const blob = new Blob([file.data], { type: file.data.type })
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = filename
    link.click()
  }
}
